import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, GeoJSON, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { FiX, FiMapPin } from 'react-icons/fi';
import { BasemapLayers, BasemapToggle } from './MapBasemaps';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';

// Pull every vertex out of a boundary GeoJSON as [lat,lng] (GeoJSON stores [lng,lat]).
function extractVertices(geojson) {
  const verts = [];
  const walk = (c) => {
    if (typeof c[0] === 'number') verts.push([c[1], c[0]]);
    else c.forEach(walk);
  };
  (geojson?.features || []).forEach((f) => { if (f.geometry) walk(f.geometry.coordinates); });
  return verts;
}

// Snap a click to the nearest boundary corner if it's close (~40m), else use the raw click.
function snapToVertex(latlng, vertices) {
  let best = null, bestD = Infinity;
  for (const [vlat, vlng] of vertices) {
    const d = (vlat - latlng.lat) ** 2 + (vlng - latlng.lng) ** 2;
    if (d < bestD) { bestD = d; best = [vlat, vlng]; }
  }
  const THRESH = 0.0004; // ~40m in degrees
  if (best && bestD <= THRESH * THRESH) return { lat: best[0], lng: best[1] };
  return { lat: latlng.lat, lng: latlng.lng };
}

function pinIcon(label, color) {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:#fff;border:2px solid #fff;border-radius:50%;
      width:24px;height:24px;display:flex;align-items:center;justify-content:center;
      font-size:12px;font-weight:700;box-shadow:0 1px 4px rgba(0,0,0,.5)">${label}</div>`,
    iconSize: [24, 24], iconAnchor: [12, 12],
  });
}

function FitView({ vertices, photos }) {
  const map = useMap();
  useEffect(() => {
    const pts = [...vertices, ...photos.filter(p => p.photo_lat != null).map(p => [p.photo_lat, p.photo_lng])];
    if (pts.length === 1) { map.setView(pts[0], 18); return; }
    if (pts.length > 1) map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 19 });
  }, [vertices, photos, map]);
  return null;
}

function ClickCapture({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
}

export default function PlacePhotosModal({ jobId, onClose, onSaved }) {
  const [basemap, setBasemap] = useState('satellite');
  const [boundary, setBoundary] = useState(null);
  const [vertices, setVertices] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [pending, setPending] = useState(null); // {lat,lng} awaiting a photo choice
  const [saving, setSaving] = useState(false);

  const authCfg = useCallback(async () => {
    const token = await getAuthToken();
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  }, []);

  const loadPhotos = useCallback(async () => {
    try {
      const cfg = await authCfg();
      const res = await axios.get(`${API_URL}/jobs/${jobId}/photos`, cfg);
      setPhotos(res.data?.photos || []);
    } catch (e) { /* ignore */ }
  }, [jobId, authCfg]);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await authCfg();
        const res = await axios.get(`${API_URL}/jobs/${jobId}/boundary`, cfg);
        setBoundary(res.data);
        setVertices(extractVertices(res.data));
      } catch (e) { setBoundary(null); setVertices([]); } // no boundary uploaded — still placeable
    })();
    loadPhotos();
  }, [jobId, authCfg, loadPhotos]);

  const handleMapClick = (latlng) => setPending(snapToVertex(latlng, vertices));

  const placePhoto = async (photo) => {
    if (!pending) return;
    setSaving(true);
    try {
      const cfg = await authCfg();
      await axios.put(`${API_URL}/jobs/${jobId}/photos/${photo.id}`,
        { photo_lat: pending.lat, photo_lng: pending.lng, show_on_portal: true }, cfg);
      setPending(null);
      await loadPhotos();
      if (onSaved) onSaved();
    } catch (e) { alert('Could not place the photo. Try again.'); }
    setSaving(false);
  };

  const placed = photos.filter(p => p.photo_lat != null);
  const center = vertices[0] || (placed[0] ? [placed[0].photo_lat, placed[0].photo_lng] : [34.5, -84.0]);

  return (
    <div className="qv-modal-overlay" onClick={onClose}>
      <div className="qv-modal" style={{ maxWidth: 720, width: '95%' }} onClick={(e) => e.stopPropagation()}>
        <div className="qv-modal-head">
          <h3>Place Photos on Map</h3>
          <button className="btn-ghost" onClick={onClose}><FiX /> Close</button>
        </div>
        <div className="qv-modal-body">
          <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 10 }}>
            {pending
              ? 'Now pick the photo taken at that corner 👇'
              : vertices.length
                ? 'Tap a boundary corner (it snaps to the exact surveyed point), then choose the photo.'
                : 'No boundary uploaded, so tap anywhere to place a photo. (Upload a KMZ to snap to corners.)'}
          </div>
          <div style={{ height: 360, borderRadius: 8, overflow: 'hidden', position: 'relative', marginBottom: 12 }}>
            <MapContainer center={center} zoom={17} style={{ height: '100%', width: '100%' }}>
              <BasemapLayers basemap={basemap} />
              <FitView vertices={vertices} photos={photos} />
              <ClickCapture onClick={handleMapClick} />
              {boundary && boundary.features && boundary.features.length > 0 && (
                <GeoJSON data={boundary} style={{ color: '#ff00ff', weight: 3, fillColor: '#ff00ff', fillOpacity: 0.06 }} />
              )}
              {placed.map((p, i) => (
                <Marker key={p.id} position={[p.photo_lat, p.photo_lng]}
                  icon={pinIcon(i + 1, p.gps_source === 'manual' ? '#16a34a' : '#2563eb')} />
              ))}
              {pending && <Marker position={[pending.lat, pending.lng]} icon={pinIcon('?', '#f59e0b')} />}
            </MapContainer>
            <BasemapToggle basemap={basemap} setBasemap={setBasemap} />
          </div>

          {pending && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Photo for {pending.lat.toFixed(6)}, {pending.lng.toFixed(6)}
                <button onClick={() => setPending(null)} style={{ marginLeft: 8, fontSize: 12, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>cancel</button>
              </div>
              {photos.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No photos on this job yet.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                  {photos.map(p => (
                    <button key={p.id} disabled={saving} onClick={() => placePhoto(p)}
                      style={{ padding: 0, border: p.photo_lat != null ? '2px solid #16a34a' : '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', background: '#fff', cursor: saving ? 'wait' : 'pointer', position: 'relative' }}
                      title={p.photo_lat != null ? 'Already placed — click to move here' : 'Place here'}>
                      <img src={`${API_BASE_URL || ''}${p.url}`} alt={p.caption || ''} style={{ width: '100%', height: 70, objectFit: 'cover', display: 'block' }} />
                      {p.photo_lat != null && (
                        <span style={{ position: 'absolute', top: 2, right: 2, background: '#16a34a', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FiMapPin size={9} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!pending && placed.length > 0 && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {placed.length} photo{placed.length === 1 ? '' : 's'} placed. Green pins were hand-placed on the map; blue pins came from photo GPS. Tap a corner to place more.
            </div>
          )}
        </div>
        <div className="qv-modal-foot">
          <button className="btn btn-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
